import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Participant, ParticipantKind, RemoteParticipant, Room, RoomEvent } from 'livekit-client';
import { v4 as uuidv4 } from 'uuid';
import { CALLER_AUDIO_CAPTURE } from '@shared/constants/audioCapture';
import { VITE_LIVEKIT_HOST } from '@shared/constants/environments';
import {
  SUPERVISOR_ATTRIBUTE,
  SUPERVISOR_MODE,
  SUPERVISOR_SIGNAL_TOPIC,
  SUPERVISOR_TIMEOUTS,
} from '@shared/constants/supervisor.constants';
import {
  useLazyGetSupervisorAccessTokenQuery,
  useSetSupervisorModeMutation,
} from '@shared/store/api/supervisorApi';
import { resolveWebSocketUrl } from '@shared/utils/serviceUrl';

export type SupervisorConnectionState = 'idle' | 'connecting' | 'connected' | 'joining' | 'joined' | 'error';

/** Signal is a liveness probe, so its states are deliberately coarse. */
export type SupervisorSignalState = 'idle' | 'sending' | 'awaiting-ack' | 'acked' | 'timed-out';

/**
 * `awaiting-agent` is the important one: the mode is already `barge` server-side but the
 * agent has not confirmed silence, so the microphone must stay shut. `unconfirmed` means
 * the operator overrode that wait deliberately.
 */
export type SupervisorTakeOverState =
  | 'idle'
  | 'requesting'
  | 'awaiting-agent'
  | 'active'
  | 'unconfirmed'
  | 'timed-out'
  | 'error';

export interface SupervisorSession {
  room: Room;
  connection: SupervisorConnectionState;
  connectionError?: string;
  isJoined: boolean;
  /** Room is connected and transcription flows; take-over still requires Join. */
  isRoomConnected: boolean;

  signal: SupervisorSignalState;
  sendSignal: () => Promise<void>;

  takeOver: SupervisorTakeOverState;
  isMicEnabled: boolean;

  join: () => Promise<void>;
  leave: () => Promise<void>;
  requestTakeOver: () => Promise<void>;
  /** Opens the mic without the agent's confirmation. Deliberately separate from requestTakeOver. */
  forceTakeOver: () => Promise<void>;
  releaseTakeOver: () => Promise<void>;
}

/**
 * `navigator.mediaDevices` is undefined outside a secure context - plain http on anything
 * that is not localhost. Both take-over paths check this before announcing barge, so the
 * agent is never silenced for a microphone that was never going to open.
 *
 * `typeof` rather than a truthiness check: the DOM types declare `getUserMedia` as always
 * present, so TypeScript rejects the shorter form as a condition that can never be false.
 */
const microphoneUnavailableReason = (): string | null =>
  typeof navigator.mediaDevices?.getUserMedia === 'function'
    ? null
    : 'Microphone unavailable: the page must be served over HTTPS or from localhost.';

const isAgentParticipant = (participant: Participant): boolean =>
  participant.kind === ParticipantKind.AGENT || participant.identity.includes('agent');

const readAgentAttributes = (room: Room): Record<string, string> => {
  const agent = Array.from(room.remoteParticipants.values()).find(isAgentParticipant);

  return agent?.attributes ?? {};
};

/**
 * Drives the v1 supervisor flow: Join -> Signal -> Take over.
 *
 * Nothing happens until the operator asks for it - unlike the legacy hidden viewer, this
 * identity is visible in the room from the moment it connects. Agent state is read off the
 * agent's own participant rather than polled, since the browser is already in the room.
 */
export const useSupervisorSession = (
  callId: string,
  callStatus?: string,
  onError?: <T>(error: T) => void,
): SupervisorSession => {
  /**
   * Replaced on every disconnect, never reconnected: LiveKit rejects a rejoin on a Room
   * instance that has already been connected, and the error it surfaces names the token
   * rather than the instance. Every exit funnels through `RoomEvent.Disconnected`, so
   * swapping it there covers Leave, the customer hanging up and a dropped network alike.
   */
  const [room, setRoom] = useState(() => new Room({ audioCaptureDefaults: CALLER_AUDIO_CAPTURE }));

  const [connection, setConnection] = useState<SupervisorConnectionState>('idle');
  const [connectionError, setConnectionError] = useState<string | undefined>();

  const [signal, setSignal] = useState<SupervisorSignalState>('idle');
  const [lastSignalId, setLastSignalId] = useState<string | undefined>();

  const [takeOver, setTakeOver] = useState<SupervisorTakeOverState>('idle');
  const [agentSilenced, setAgentSilenced] = useState(false);
  const [isMicEnabled, setIsMicEnabled] = useState(false);

  const [agentAttributes, setAgentAttributes] = useState<Record<string, string>>({});

  const signalTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const connectionTargetRef = useRef<'connected' | 'joined'>('connected');
  // `leave` runs before releaseTakeOver is declared, so it reaches it through a ref.
  const releaseTakeOverRef = useRef<() => Promise<void>>(async () => {});
  // A failed passive connect latches here. Without it a rejected token loops:
  // connect fails -> RoomEvent.Disconnected resets the state to idle -> the effect
  // reads idle as permission to try again. Only an explicit Join clears it.
  const autoConnectBlockedRef = useRef(false);

  const [fetchAccessToken] = useLazyGetSupervisorAccessTokenQuery();
  const [setModeRequest] = useSetSupervisorModeMutation();

  const clearTimers = useCallback(() => {
    clearTimeout(signalTimerRef.current);
    clearTimeout(silenceTimerRef.current);
  }, []);

  // Token attributes are already present on ParticipantConnected and fire no
  // ParticipantAttributesChanged, so the initial snapshot must be read explicitly.
  useEffect(() => {
    const syncAgentAttributes = () => setAgentAttributes(readAgentAttributes(room));

    const onAttributesChanged = (_changed: Record<string, string>, participant: Participant) => {
      if (isAgentParticipant(participant)) {
        syncAgentAttributes();
      }
    };

    const onParticipantChanged = (participant: RemoteParticipant) => {
      if (isAgentParticipant(participant)) {
        syncAgentAttributes();
      }
    };

    room.on(RoomEvent.Connected, syncAgentAttributes);
    room.on(RoomEvent.ParticipantAttributesChanged, onAttributesChanged);
    room.on(RoomEvent.ParticipantConnected, onParticipantChanged);
    room.on(RoomEvent.ParticipantDisconnected, onParticipantChanged);

    return () => {
      room.off(RoomEvent.Connected, syncAgentAttributes);
      room.off(RoomEvent.ParticipantAttributesChanged, onAttributesChanged);
      room.off(RoomEvent.ParticipantConnected, onParticipantChanged);
      room.off(RoomEvent.ParticipantDisconnected, onParticipantChanged);
    };
  }, [room]);

  useEffect(() => {
    const onDisconnected = () => {
      clearTimers();
      setConnection('idle');
      setTakeOver('idle');
      setSignal('idle');
      setIsMicEnabled(false);
      setAgentSilenced(false);
      setAgentAttributes({});
      // Discard the spent instance so the next Join starts from a clean one.
      setRoom(new Room({ audioCaptureDefaults: CALLER_AUDIO_CAPTURE }));
    };

    const onDeviceFailure = (error: Error) => {
      setConnectionError(error.message);
      onError?.(error);
    };

    room.on(RoomEvent.Disconnected, onDisconnected);
    room.on(RoomEvent.MediaDevicesError, onDeviceFailure);

    return () => {
      room.off(RoomEvent.Disconnected, onDisconnected);
      room.off(RoomEvent.MediaDevicesError, onDeviceFailure);
    };
  }, [room, onError, clearTimers]);

  // Matching on the id stops a reconnecting agent's replayed attribute from reading as a
  // fresh success.
  const ackedSignalId = agentAttributes[SUPERVISOR_ATTRIBUTE.AGENT_SIGNAL_ACK];

  useEffect(() => {
    if (!lastSignalId || signal !== 'awaiting-ack') return;

    if (ackedSignalId === lastSignalId) {
      clearTimeout(signalTimerRef.current);
      setSignal('acked');
    }
  }, [ackedSignalId, lastSignalId, signal]);

  // Derived from the agent's current attribute, never latched locally.
  const agentSilencedAttribute = agentAttributes[SUPERVISOR_ATTRIBUTE.AGENT_SILENCED];

  useEffect(() => {
    setAgentSilenced(agentSilencedAttribute === 'true');
  }, [agentSilencedAttribute]);

  useEffect(() => {
    if (takeOver !== 'awaiting-agent' || !agentSilenced) return;

    clearTimeout(silenceTimerRef.current);

    room.localParticipant
      .setMicrophoneEnabled(true)
      .then(() => {
        setIsMicEnabled(true);
        setTakeOver('active');
      })
      .catch((error: Error) => {
        setTakeOver('error');
        setConnectionError(error.message);
        onError?.(error);

        // The agent is already silent here, so leaving the mode alone strands the call
        // with a muted agent and a supervisor who cannot speak - the customer would hear
        // nobody at all.
        setModeRequest({
          callId,
          mode: SUPERVISOR_MODE.LISTEN,
          identity: room.localParticipant.identity,
        })
          .unwrap()
          .catch(onError);
      });
  }, [takeOver, agentSilenced, room, onError, callId, setModeRequest]);

  //One connection serves both roles, `connected` is the passive viewer the operator gets without asking
  const connectToRoom = useCallback(
    async (target: 'connected' | 'joined') => {
      connectionTargetRef.current = target;

      if (room.state === 'connected') {
        setConnection(target);
        return;
      }

      if (room.state === 'connecting') {
        setConnection(target === 'joined' ? 'joining' : 'connecting');
        return;
      }

      setConnection(target === 'joined' ? 'joining' : 'connecting');
      setConnectionError(undefined);

      try {
        const { token } = await fetchAccessToken({
          callId,
          mode: SUPERVISOR_MODE.LISTEN,
        }).unwrap();

        await room.connect(resolveWebSocketUrl(VITE_LIVEKIT_HOST || 'ws://localhost:7880'), token);

        await room.localParticipant.setMicrophoneEnabled(false);

        setIsMicEnabled(false);
        setConnection(connectionTargetRef.current);
      } catch (error) {
        if (target === 'connected') {
          autoConnectBlockedRef.current = true;
        }
        setConnection('error');
        setConnectionError(error instanceof Error ? error.message : 'Failed to join the call');
        onError?.(error);
      }
    },
    [callId, fetchAccessToken, room, onError],
  );

  const join = useCallback(async () => {
    if (connection === 'joining' || connection === 'joined') return;

    autoConnectBlockedRef.current = false;
    await connectToRoom('joined');
  }, [connection, connectToRoom]);

  useEffect(() => {
    if (callStatus !== 'in-progress' || connection !== 'idle' || autoConnectBlockedRef.current) {
      return;
    }

    connectToRoom('connected');
  }, [callStatus, connection, connectToRoom]);

  // Leave steps out of the session, not out of the room. 
  const leave = useCallback(async () => {
    clearTimers();

    if (takeOver === 'active' || takeOver === 'unconfirmed') {
      await releaseTakeOverRef.current();
    }

    setConnection(room.state === 'connected' ? 'connected' : 'idle');
  }, [room, clearTimers, takeOver]);

  const sendSignal = useCallback(async () => {
    if (connection !== 'joined') return;

    setSignal('sending');
    clearTimeout(signalTimerRef.current);

    const signalId = uuidv4();

    try {
      await room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify({ signalId })),
        { reliable: true, topic: SUPERVISOR_SIGNAL_TOPIC },
      );

      setLastSignalId(signalId);
      setSignal('awaiting-ack');

      signalTimerRef.current = setTimeout(() => {
        setSignal((current) => (current === 'awaiting-ack' ? 'timed-out' : current));
      }, SUPERVISOR_TIMEOUTS.SIGNAL_ACK_MS);
    } catch (error) {
      setSignal('timed-out');
      onError?.(error);
    }
  }, [connection, room, onError]);

  const requestTakeOver = useCallback(async () => {
    if (connection !== 'joined' || takeOver === 'active') return;

    // Before telling the agent to go quiet.
    const unavailable = microphoneUnavailableReason();

    if (unavailable) {
      setTakeOver('error');
      setConnectionError(unavailable);
      onError?.(new Error(unavailable));
      return;
    }

    setTakeOver('requesting');
    clearTimeout(silenceTimerRef.current);

    try {
      await setModeRequest({
        callId,
        mode: SUPERVISOR_MODE.BARGE,
        identity: room.localParticipant.identity,
      }).unwrap();

      // The mic stays shut until the agent confirms silence - announcing barge and
      // unmuting together would let the customer hear two voices at once.
      setTakeOver('awaiting-agent');

      silenceTimerRef.current = setTimeout(() => {
        setTakeOver((current) => (current === 'awaiting-agent' ? 'timed-out' : current));
      }, SUPERVISOR_TIMEOUTS.AGENT_SILENCE_MS);
    } catch (error) {
      setTakeOver('error');
      setConnectionError(error instanceof Error ? error.message : 'Failed to take over');
      onError?.(error);
    }
  }, [callId, connection, takeOver, room, setModeRequest, onError]);

  const forceTakeOver = useCallback(async () => {
    clearTimeout(silenceTimerRef.current);

    const unavailable = microphoneUnavailableReason();

    if (unavailable) {
      setTakeOver('error');
      setConnectionError(unavailable);
      onError?.(new Error(unavailable));
      return;
    }

    try {
      await room.localParticipant.setMicrophoneEnabled(true);
      setIsMicEnabled(true);
      // Not `active`: the agent never confirmed it went quiet, so the UI must keep
      // saying so rather than presenting this as a clean take-over.
      setTakeOver('unconfirmed');
    } catch (error) {
      setTakeOver('error');
      setConnectionError(error instanceof Error ? error.message : 'Failed to enable microphone');
      onError?.(error);
    }
  }, [room, onError]);

  const releaseTakeOver = useCallback(async () => {
    clearTimeout(silenceTimerRef.current);

    try {
      // Mic first, then mode - the reverse order lets the agent speak over the operator.
      await room.localParticipant.setMicrophoneEnabled(false);
      setIsMicEnabled(false);

      await setModeRequest({
        callId,
        mode: SUPERVISOR_MODE.LISTEN,
        identity: room.localParticipant.identity,
      }).unwrap();
      setTakeOver('idle');
    } catch (error) {
      setTakeOver('error');
      setConnectionError(error instanceof Error ? error.message : 'Failed to hand back');
      onError?.(error);
    }
  }, [callId, room, setModeRequest, onError]);

  useEffect(() => {
    releaseTakeOverRef.current = releaseTakeOver;
  }, [releaseTakeOver]);

  useEffect(() => {
    autoConnectBlockedRef.current = false;
  }, [callId]);

  useEffect(
    () => () => {
      clearTimers();

      if (room.state !== 'disconnected') {
        room.disconnect();
      }
    },
    [room, clearTimers],
  );

  return useMemo(
    () => ({
      room,
      connection,
      connectionError,
      isJoined: connection === 'joined',
      isRoomConnected: connection === 'connected' || connection === 'joined',
      signal,
      sendSignal,
      takeOver,
      isMicEnabled,
      join,
      leave,
      requestTakeOver,
      forceTakeOver,
      releaseTakeOver,
    }),
    [
      room,
      connection,
      connectionError,
      signal,
      sendSignal,
      takeOver,
      isMicEnabled,
      join,
      leave,
      requestTakeOver,
      forceTakeOver,
      releaseTakeOver,
    ],
  );
};
